import React, { useState, useRef, useEffect } from 'react'
import { withRouter, RouteComponentProps } from 'react-router'

import { NavEventArgs, NavEvent } from 'common/events/nav-event-types'

import {
    StyledNav,
    StyledNavWrapper,
    StyledCloseButton,
    StyledButtonText,
    StyledIconCross,
    StyledNavList,
    StyledNavWrapperClass,
} from 'component-library/navigation/FlyoutNav/FlyoutNav.styled'
import { TheWestNavigation } from 'component-library/routing/route-nav-types'

import { ThemedRender } from 'component-library/__styling/themed-render'
import { useOnClickOutside } from 'component-library/__helpers/on-click-outside'
import { KeyCode } from 'component-library/__helpers/key-code'
import { isCurrent } from 'common/helpers/link-utils'
import { FlyoutNavActions } from './components/FlyoutNavActions/FlyoutNavActions'
import { usePageEvents } from 'common/context-providers/pageEvent-context'
import { FlyoutNavItem } from './components/FlyoutNavItem/FlyoutNavItem'
import { TheWestReturn } from '../TheWestReturn/TheWestReturn'
import { Product } from 'component-library/__product'
import { useProduct } from 'common/hooks/useProduct'
import {
    StyledActionLinkOutlineButton,
    StyledActionButton,
} from './components/FlyoutNavActions/FlyoutNavActions.styled'
import { AuthenticationProvider } from 'component-library/user-registration/authentication/AuthenticationProvider'
import { createSubscribeLinkClickEvent } from 'component-library/user-registration/subscribe/createSubscribeClickLinkHandler'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'
import { SubscribeEvent } from 'common/events/subscribeEventTypes'
import { DataLayerEventName } from 'common/helpers/DataLayerEventName'
import { NavItem } from 'common/navigation'

function getNavName(product: Product) {
    switch (product) {
        case 'thewest': {
            return 'Flyout.TheWest'
        }
        case 'sevennews': {
            return 'Watch.SevenNews'
        }
        default: {
            return
        }
    }
}

export interface CommonProps {
    includeSubNav?: boolean
    navLinks: TheWestNavigation
    section: TheWestSection
    onEvent: (event: NavEvent | SubscribeEvent) => void
    mainNavOpen: boolean
    closeNavigation: () => void
}

export interface TestingProps {
    startOpen?: boolean
}

export type Props = CommonProps & TestingProps

export type InternalProps = Props & RouteComponentProps<any>

export const InternalFlyoutNav: React.FC<InternalProps> = props => {
    const { navLinks, mainNavOpen, section, closeNavigation, onEvent, startOpen, location } = props
    const product = useProduct()
    const navSection = section !== undefined ? section : 'default'
    const [impression, setImpression] = useState(false)
    const ref = useRef<HTMLElement | null>(null)
    const wrapperRef = useRef<HTMLElement | null>(null)

    const handleClose = () => {
        if (mainNavOpen) {
            closeNavigation()
        }
    }

    const handleClick = (args: NavEventArgs) => {
        onEvent({
            type: DataLayerEventName.navClicked,
            originator: 'FlyoutNav',
            payload: {
                navName: getNavName(product),
                navLocation: 'Header',
                navPos: args.navPos,
                navText: args.navText,
                navLink: args.navLink,
                navPath: args.navParent ? `${args.navParent} > ${args.navText}` : `${args.navText}`,
            },
        })

        props.closeNavigation()
    }

    const handleKeyPress = (evt: React.KeyboardEvent) => {
        if (evt.which === KeyCode.ESCAP) {
            handleClose()
        }
    }

    useOnClickOutside(ref, handleClose, {
        enabled: mainNavOpen,
        filterClasses: ['header-action-nav-trigger', StyledNavWrapperClass],
    })

    // FlyoutNav should only trigger impression once per page load.
    usePageEvents(() => {
        setImpression(true)
    }, ['page-load-complete'])

    useEffect(() => {
        const wrapperCurrent = wrapperRef.current

        if (!wrapperCurrent) {
            return
        }

        const handleTransitionEnd = () => {
            if (!mainNavOpen && wrapperRef.current) {
                wrapperRef.current.classList.remove('isOpen')
            }
        }

        if (mainNavOpen) {
            wrapperCurrent.classList.add('isOpen')
            requestAnimationFrame(() => {
                wrapperCurrent && wrapperCurrent.classList.add('isTransitioning')
            })
        } else {
            wrapperCurrent.classList.remove('isTransitioning')
        }
        wrapperCurrent.addEventListener('transitionend', handleTransitionEnd)

        return function flyoutTransitionCleanup() {
            if (wrapperCurrent) {
                wrapperCurrent.removeEventListener('transitionend', handleTransitionEnd)
            }
        }
    }, [mainNavOpen])

    useEffect(() => {
        if (mainNavOpen && ref && ref.current) {
            ref.current.focus()
            if (impression) {
                onEvent({
                    type: DataLayerEventName.navAvailable,
                    originator: getNavName(product) || '',
                    payload: {},
                })

                setImpression(false)
            }
        }
    }, [mainNavOpen, impression, onEvent, product])

    const navItems = navLinks[section]

    return (
        <StyledNav onKeyDown={handleKeyPress} tabIndex={-1} role="navigation" ref={ref}>
            <ThemedRender
                thewest={() => (
                    <StyledNavWrapper ref={wrapperRef}>
                        <StyledCloseButton type="button" onClick={handleClose}>
                            <StyledButtonText>Close navigation menu</StyledButtonText>
                            <StyledIconCross />
                        </StyledCloseButton>

                        <AuthenticationProvider onEvent={onEvent}>
                            {({ onLoginClick, onLogoutClick, onSubscribeClick }) => (
                                <FlyoutNavActions
                                    LogoutAction={() => (
                                        <StyledActionLinkOutlineButton
                                            type="button"
                                            onClick={onLogoutClick}
                                        >
                                            Log Out
                                        </StyledActionLinkOutlineButton>
                                    )}
                                    LoginAction={() => (
                                        <StyledActionLinkOutlineButton onClick={onLoginClick}>
                                            Log In
                                        </StyledActionLinkOutlineButton>
                                    )}
                                    SubscribeAction={() => (
                                        <StyledActionButton
                                            onClick={() => {
                                                createSubscribeLinkClickEvent(
                                                    'flyout-nav',
                                                    onEvent
                                                )()
                                                onSubscribeClick()
                                            }}
                                        >
                                            Subscribe
                                        </StyledActionButton>
                                    )}
                                />
                            )}
                        </AuthenticationProvider>

                        <StyledNavList>
                            <FlyoutNavItem
                                navPos={0}
                                section={navSection}
                                navItem={{
                                    name: 'Home',
                                    link: '/',
                                }}
                                isCurrent={isCurrent(
                                    location,
                                    section !== 'default' ? `/${navSection}` : '/',
                                    { exact: true }
                                )}
                                onClick={handleClick}
                                onEvent={onEvent}
                                startOpen={startOpen}
                            />
                            {navItems &&
                                navItems.map((navItem: NavItem, index: number) => {
                                    return (
                                        <FlyoutNavItem
                                            key={navItem.name}
                                            // +1 because of home
                                            navPos={index + 1}
                                            section={navSection}
                                            navItem={navItem}
                                            showToggleButton={true}
                                            onClick={handleClick}
                                            onEvent={onEvent}
                                            startOpen={startOpen}
                                            isCurrent={isCurrent(location, navItem.link)}
                                        />
                                    )
                                })}
                            {section !== 'default' && (
                                <FlyoutNavItem
                                    linkStyle={'standard'}
                                    navPos={navItems ? navItems.length + 1 : 1}
                                    section={navSection}
                                    parentMarker={() => (
                                        <TheWestReturn hideText={true} darkTheme={true} />
                                    )}
                                    navItem={{
                                        name: 'thewest.com.au',
                                        link: 'https://thewest.com.au',
                                        newWindow: false, // explicit
                                    }}
                                    isCurrent={false}
                                    onClick={handleClick}
                                    onEvent={onEvent}
                                    startOpen={startOpen}
                                />
                            )}
                        </StyledNavList>
                    </StyledNavWrapper>
                )}
            />
        </StyledNav>
    )
}

InternalFlyoutNav.displayName = 'InternalFlyoutNav'

export const FlyoutNav = withRouter(InternalFlyoutNav)
FlyoutNav.displayName = 'FlyoutNav'

export interface ProviderChildProps {
    mainNavOpen: boolean
    origin?: HTMLElement
    closeNavigation: () => void
    openNavigation: () => void
}

interface ProviderProps {
    children: (childProps: ProviderChildProps) => React.ReactElement<any> | null
}

export const FlyoutNavStateProvider: React.FC<ProviderProps> = props => {
    const [open, setOpen] = useState(false)
    const [origin, setOrigin] = useState<HTMLElement | null>(null)

    function isHTMLElement(element: Element | null): element is HTMLElement {
        return element && 'focus' in element ? true : false
    }

    return props.children({
        mainNavOpen: open,
        closeNavigation: () => {
            if (origin) {
                origin.focus()
                setOrigin(null)
            }
            setOpen(false)
        },
        openNavigation: () => {
            setOrigin(isHTMLElement(document.activeElement) ? document.activeElement : null)
            setOpen(true)
        },
    })
}
